import { css, html, LitElement } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";

import {
    formatDate,
    getMonthRange,
    GetRevenueEntriesParams,
    RevenueType,
    revenueTypeIcon,
    revenueTypeLabel,
    taxKeyLabel,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { alert } from "../../elements/alert-dialog";
import "../../elements/scroller";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import "./components/revenue-item";

import { Task } from "@lit/task";
import "../../elements/entity-multi-select";
import type { EntityMultiSelect } from "../../elements/entity-multi-select";
import { app } from "../../init";
import { shared } from "../../styles";

const allRevenueTypes = [
    RevenueType.Adjustment,
    RevenueType.BankDeposit,
    RevenueType.Cashless,
    RevenueType.Debt,
    RevenueType.Deposit,
    RevenueType.Expense,
    RevenueType.Other,
    RevenueType.PayAdvance,
    RevenueType.Sales,
].sort((a, b) => revenueTypeLabel(a)?.localeCompare(revenueTypeLabel(b) || "") || 0);

@customElement("ptc-revenues-all")
export class RevenuesAll extends Routing(StateMixin(LitElement)) {
    routePattern = /^revenues\/all/;

    static styles = [
        shared,
        css`
            :host {
                display: flex;
                flex-direction: column;
            }

            .scroller {
                flex: 1;
                overflow: auto;
            }

            thead {
                position: sticky;
                top: 0;
                z-index: 2;
            }

            tbody {
                margin-bottom: 1em;
                box-sizing: border-box;
            }

            tr {
                align-items: center;
                width: 100%;
            }

            tbody > tr:not(:last-of-type) {
                border-bottom: solid 1px var(--shade-2);
            }

            th,
            td {
                padding: 0.5em;
                vertical-align: middle;
            }

            thead {
                font-weight: bold;
                background: var(--color-bg);
                text-align: left;
            }

            tr > td,
            tr > th {
                text-align: left;
            }

            tr > td:last-child,
            tr > th:last-child {
                text-align: right;
            }

            .no-border {
                border: none;
            }

            .positive {
                color: var(--color-positive);
            }

            .negative {
                color: var(--color-negative);
            }
        `,
    ];

    public get routeTitle() {
        return (this.venue && `Alle Umsätze: ${this.venue.name}`) || undefined;
    }

    protected handleRoute() {
        void this._entriesTask.run();
    }

    @query("#typeSelector")
    private _typesSelector: EntityMultiSelect<RevenueType>;

    @state()
    private _revenueTypes: RevenueType[] = allRevenueTypes;

    @query("#filterInput")
    private _filterInput: HTMLInputElement;

    @state()
    private _filterString = "";

    private _updateFilters() {
        this._filterString = this._filterInput.value.toLowerCase();
    }

    private _clearFilter() {
        this._filterString = this._filterInput.value = "";
    }

    private _updateTypes() {
        this._revenueTypes = this._typesSelector.selected;
        if (!this._revenueTypes.length) {
            this._revenueTypes = allRevenueTypes;
        }
        this.requestUpdate();
    }

    private _entriesTask = new Task(this, {
        task: async ([venueId, date]) => {
            if (!venueId || !date) {
                return [];
            }

            try {
                return await app.api.getRevenueEntries(
                    new GetRevenueEntriesParams({
                        from: getMonthRange(this.date).from,
                        to: getMonthRange(this.date).to,
                        venue: venueId,
                    })
                );
            } catch (e) {
                void alert(e.message, { type: "warning" });
                return [];
            }
        },
        args: () => [this.venue?.id, this.date] as const,
    });

    protected render(): unknown {
        return html`
            <div class="center-aligning horizontal layout padded border-top border-bottom">
                <ptc-ui-icon name="icon.action.filter"></ptc-ui-icon>
                <ptc-entity-multi-select
                    id="typeSelector"
                    addLabel="Filter Hinzufügen..."
                    .existing=${allRevenueTypes}
                    .getLabel=${(type: RevenueType) => revenueTypeLabel(type)}
                    .getIcon=${(type: RevenueType) => revenueTypeIcon(type)}
                    @change=${() => this._updateTypes()}
                    class="smaller no-border stretch"
                ></ptc-entity-multi-select>
                <div class="filter-input right icon input smaller">
                    <input
                        id="filterInput"
                        type="text"
                        placeholder="Suchen..."
                        @input=${() => this._updateFilters()}
                        autocomplete="off"
                    />
                    ${this._filterString
                        ? html`<ptc-ui-icon
                              name="icon.action.clear"
                              class="click icon"
                              @click=${this._clearFilter}
                          ></ptc-ui-icon>`
                        : html`<ptc-ui-icon
                              name="icon.action.search"
                              class="icon"
                              @click=${this._clearFilter}
                          ></ptc-ui-icon>`}
                </div>
            </div>
            <div class="scroller">
                <div class="double-padded">
                    <table class="fill-horizontally">
                        <thead>
                            <tr>
                                <th>Art</th>
                                <th>Datum</th>
                                <th>Text</th>
                                <th>Belegnummer</th>
                                <th>Steuersatz</th>
                                <th>Konto</th>
                                <th>Kostenstelle</th>
                                <th>Betrag</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._entriesTask.render({
                                pending: () => this._renderLoading(),
                                complete: (entries) =>
                                    repeat(
                                        entries
                                            .filter((entry) => this._revenueTypes.includes(entry.type))
                                            .filter(
                                                (entry) =>
                                                    this._filterString.trim() === "" ||
                                                    entry.text.toLowerCase().includes(this._filterString)
                                            ),
                                        (entry) => entry.id,
                                        (entry) =>
                                            html`<tr class="row">
                                                <td title="${revenueTypeLabel(entry.type) || ""}">
                                                    <ptc-ui-icon
                                                        name="${revenueTypeIcon(entry.type) || ""}"
                                                    ></ptc-ui-icon>
                                                </td>
                                                <td>${formatDate(entry.date)}</td>
                                                <td>${entry.text}</td>
                                                <td>${entry.receipt}</td>
                                                <td>${taxKeyLabel(entry.taxKey)}</td>
                                                <td>${entry.ledger}</td>
                                                <td>${entry.costCenter}</td>
                                                <td>
                                                    <ptc-ui-balance iconStyle="plus-minus" .value="${entry.amount}">
                                                    </ptc-ui-balance>
                                                </td>
                                            </tr>`
                                    ),
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    }

    private _renderLoading() {
        return html`<div class="fullbleed center-aligning center-justifying vertical layout scrim">
            <ptc-ui-spinner active></ptc-ui-spinner>
        </div>`;
    }
}
